const OFFLINE_VERIFIER_KEY = "cached_password_verifier";
const OFFLINE_SALT_KEY = "cached_password_salt";
const OFFLINE_USERNAME_KEY = "cached_username";
const OFFLINE_SESSION_KEY = "user_session";
const PBKDF2_ITERATIONS = 200_000;

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function createOfflineSalt(): string {
  return bytesToBase64(crypto.getRandomValues(new Uint8Array(16)));
}

async function deriveOfflineVerifier(password: string, saltB64: string): Promise<string> {
  if (!window.crypto?.subtle) {
    throw new Error("WebCrypto unavailable");
  }
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey("raw", enc.encode(password), "PBKDF2", false, [
    "deriveBits",
  ]);
  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: base64ToBytes(saltB64),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    256,
  );
  return bytesToBase64(new Uint8Array(bits));
}

/** Remove legacy reversible Base64 password cache if present. */
export function migrateAwayLegacyOfflineHash(): void {
  try {
    localStorage.removeItem("cached_password_hash");
  } catch {
    /* ignore */
  }
}

export async function storeOfflineCredentials(username: string, password: string): Promise<void> {
  const salt = createOfflineSalt();
  const verifier = await deriveOfflineVerifier(password, salt);
  localStorage.setItem(OFFLINE_USERNAME_KEY, username);
  localStorage.setItem(OFFLINE_SALT_KEY, salt);
  localStorage.setItem(OFFLINE_VERIFIER_KEY, verifier);
  migrateAwayLegacyOfflineHash();
}

export function hasOfflineCredentials(): boolean {
  try {
    return Boolean(
      localStorage.getItem(OFFLINE_USERNAME_KEY) &&
        localStorage.getItem(OFFLINE_SALT_KEY) &&
        localStorage.getItem(OFFLINE_VERIFIER_KEY),
    );
  } catch {
    return false;
  }
}

export type OfflineLoginResult =
  | { ok: true }
  | { ok: false; reason: "missing" | "mismatch" | "unsupported" };

export async function tryOfflineLogin(
  username: string,
  password: string,
): Promise<OfflineLoginResult> {
  migrateAwayLegacyOfflineHash();
  const savedUsername = localStorage.getItem(OFFLINE_USERNAME_KEY);
  const savedSalt = localStorage.getItem(OFFLINE_SALT_KEY);
  const savedVerifier = localStorage.getItem(OFFLINE_VERIFIER_KEY);
  if (!savedUsername || !savedSalt || !savedVerifier) {
    return { ok: false, reason: "missing" };
  }
  try {
    const candidate = await deriveOfflineVerifier(password, savedSalt);
    if (username === savedUsername && candidate === savedVerifier) {
      localStorage.setItem(OFFLINE_SESSION_KEY, "active");
      return { ok: true };
    }
    return { ok: false, reason: "mismatch" };
  } catch {
    return { ok: false, reason: "unsupported" };
  }
}

export function setSoftSessionActive(): void {
  try {
    localStorage.setItem(OFFLINE_SESSION_KEY, "active");
  } catch {
    /* ignore */
  }
}

export function clearSoftSession(): void {
  try {
    localStorage.removeItem(OFFLINE_SESSION_KEY);
  } catch {
    /* ignore */
  }
  try {
    document.cookie = "user_session=; Path=/; Max-Age=0; Secure; SameSite=Lax";
  } catch {
    /* ignore */
  }
}

export function isSoftSessionActive(): boolean {
  try {
    return localStorage.getItem(OFFLINE_SESSION_KEY) === "active";
  } catch {
    return false;
  }
}

/** Offline: flaga w localStorage. Online: sesja to HttpOnly cookie (serwer). */
export function isClientLoggedIn(): boolean {
  return !navigator.onLine && isSoftSessionActive();
}
